import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaPlus, FaGripVertical, FaLock, FaBook, FaPlayCircle, FaQuestionCircle, FaCode, FaRedo } from "react-icons/fa";
import {
  fetchChapterById,
  fetchChaptersForCourse,
  fetchTopicsForChapter,
  deleteTopic,
  reorderTopics,
} from "../../../apis/CourseApis";
import { fetchChapterSubmissions, clearChapterSubmissions } from "../../../apis/SubmissionApis";
import { fetchCourseAccess } from "../../../apis/AccessApis";
import { useAuth } from "../../Authentication/AuthContext";
import { useToastHelpers } from "../../Toasts/ToastUtils";
import SeoMeta from "../../SeoMeta";
import { SkeletonTopicList } from "../../Skeletons";
import { FREE_CHAPTER_COUNT, getChapterUnlockStatus } from "../../../config/courseUnlock";
import { computeChapterCompletionPercent } from "../../../utils/courseProgress";
import { useTheme } from "../../Theme/ThemeContext";

export default function TopicList() {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const { user, loggedInUser, isAuthenticated } = useAuth();
  const { successToast, errorToast, warningToast } = useToastHelpers();
  const { currentTheme } = useTheme();

  const [chapter, setChapter] = useState(null);
  const [topics, setTopics] = useState([]);
  const [topicsOriginal, setTopicsOriginal] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingTopic, setDeletingTopic] = useState(null);
  const [draggedTopicIndex, setDraggedTopicIndex] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const [progressByTopicId, setProgressByTopicId] = useState({});
  const [chapterUnlock, setChapterUnlock] = useState({ unlocked: true, reason: null });
  const [isClearingSubmissions, setIsClearingSubmissions] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all"); // all | theory | coding | mcq | video

  // Load chapter + topics (safe)
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        setIsLoading(true);
        const chapterData = await fetchChapterById(chapterId);
        if (!isMounted) return;
        setChapter(chapterData);

        const topicsData = await fetchTopicsForChapter(chapterId);
        if (!isMounted) return;

        if (Array.isArray(topicsData?.topics)) {
          setTopics(topicsData.topics);
          setTopicsOriginal(topicsData.topics);
        } else if (Array.isArray(topicsData)) {
          setTopics(topicsData);
          setTopicsOriginal(topicsData);
        } else {
          setTopics([]);
          setTopicsOriginal([]);
        }
        setIsLoading(false);
      } catch (err) {
        console.error("Error loading chapter:", err);
        if (isMounted) {
          errorToast("Failed to load chapter");
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [chapterId]);

  // Load user progress for this chapter (optional, auth only)
  useEffect(() => {
    let isMounted = true;

    const loadProgress = async () => {
      if (!isAuthenticated || !user?.token) {
        if (isMounted) setProgressByTopicId({});
        return;
      }

      try {
        const data = await fetchChapterSubmissions(chapterId, user.token);
        const submissions = Array.isArray(data?.submissions) ? data.submissions : Array.isArray(data) ? data : [];

        const next = {};
        for (const sub of submissions) {
          const topicId = String(sub?.topicId || sub?.topic || sub?.topicID || "");
          if (!topicId) continue;
          const score = Number.isFinite(sub?.score) ? sub.score : 0;
          if (!next[topicId]) {
            next[topicId] = { attempted: true, bestScore: score };
          } else {
            next[topicId].attempted = true;
            next[topicId].bestScore = Math.max(next[topicId].bestScore, score);
          }
        }

        if (isMounted) setProgressByTopicId(next);
      } catch (err) {
        // Progress is non-blocking; do not toast.
        console.error("Error loading chapter submissions:", err);
        if (isMounted) setProgressByTopicId({});
      }
    };

    loadProgress();
    return () => {
      isMounted = false;
    };
  }, [chapterId, isAuthenticated, user?.token]);

  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm("Are you sure you want to delete this topic?")) return;

    setDeletingTopic(topicId);
    try {
      await deleteTopic(topicId, user?.token);
      setTopics((prev) => prev.filter((t) => t.id !== topicId));
      setTopicsOriginal((prev) => prev.filter((t) => t.id !== topicId));
      successToast("Topic deleted successfully");
    } catch (err) {
      console.error("Delete topic error:", err);
      errorToast("Failed to delete topic");
    } finally {
      setDeletingTopic(null);
    }
  };

  const isAdmin = loggedInUser?.role === "admin";

  // Compute chapter index within course and whether this chapter is unlocked.
  useEffect(() => {
    let isMounted = true;

    const loadChapterAccess = async () => {
      if (!chapter) return;

      const courseId = String(chapter?.courseId || chapter?.course || "");
      if (!courseId) {
        // If we cannot determine course, do not lock.
        if (isMounted) setChapterUnlock({ unlocked: true, reason: null });
        return;
      }

      try {
        const chaptersRes = await fetchChaptersForCourse(courseId);
        const chaptersList = Array.isArray(chaptersRes?.chapters)
          ? chaptersRes.chapters
          : Array.isArray(chaptersRes)
            ? chaptersRes
            : [];

        const currentIndex = Math.max(
          0,
          chaptersList.findIndex((c) => String(c?.id || c?._id || "") === String(chapterId))
        );

        let allowed = false;
        if (user?.token) {
          const userId = user?.userId || loggedInUser?.id || loggedInUser?._id;
          if (userId) {
            try {
              const access = await fetchCourseAccess({
                courseId,
                userId,
                token: user.token,
              });
              allowed = Boolean(access?.allowed);
            } catch {
              allowed = false;
            }
          }
        }

        // Only need previous chapter progress for progressive unlock.
        const chapterCompletionPercents = [];
        if (!allowed && !isAdmin && user?.token && currentIndex > 0 && currentIndex < FREE_CHAPTER_COUNT) {
          const prev = chaptersList[currentIndex - 1];
          const prevChapterId = prev?.id || prev?._id;
          const prevPercent = await computeChapterCompletionPercent({
            chapterId: prevChapterId,
            token: user.token,
          }).catch(() => 0);

          chapterCompletionPercents[currentIndex - 1] = prevPercent;
        }

        const unlock = getChapterUnlockStatus({
          chapterIndex: currentIndex,
          isPaidUser: allowed,
          isAdmin: Boolean(isAdmin),
          chapterCompletionPercents,
        });

        if (!isMounted) return;
        setChapterUnlock(unlock);
      } catch (err) {
        console.error("Failed to compute chapter access:", err);
        if (isMounted) setChapterUnlock({ unlocked: true, reason: null });
      }
    };

    loadChapterAccess();
    return () => {
      isMounted = false;
    };
  }, [chapter, chapterId, user?.token, user?.userId, loggedInUser?.id, loggedInUser?._id, isAdmin]);

  const getTopicMeta = (topic) => {
    const type = String(topic?.type || "").toLowerCase();
    const totalPoints = Number.isFinite(topic?.points) ? topic.points : 0;
    const topicId = String(topic?.id || topic?._id || "");
    const progress = progressByTopicId[topicId] || { attempted: false, bestScore: 0 };
    const earned = Math.max(0, Math.min(progress.bestScore || 0, totalPoints));
    const attempted = Boolean(progress.attempted);
    const completed = totalPoints > 0 ? earned >= totalPoints : attempted;
    const difficulty = type === "coding" ? String(topic?.codingProblem?.difficulty || "").toLowerCase() : "";

    return { type, totalPoints, earned, attempted, completed, difficulty };
  };

  const typeLabel = (type) => {
    if (type === "mcq") return "MCQ";
    if (type === "coding") return "Coding";
    if (type === "theory") return "Theory";
    if (type === "video") return "Video";
    return type ? type : "Topic";
  };

  const getTypeStyle = (type) => {
    switch (type) {
      case "theory":
        return { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-200", icon: FaBook };
      case "video":
        return { bg: "bg-pink-100", text: "text-pink-700", border: "border-pink-200", icon: FaPlayCircle };
      case "mcq":
        return { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-200", icon: FaQuestionCircle };
      case "coding":
        return { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-200", icon: FaCode };
      default:
        return { bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-200", icon: FaBook };
    }
  };

  const getDifficultyStyle = (difficulty) => {
    switch (difficulty) {
      case "easy":
        return "bg-green-100 text-green-700 border-green-200";
      case "medium":
        return "bg-yellow-100 text-yellow-700 border-yellow-200";
      case "hard":
        return "bg-red-100 text-red-700 border-red-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const difficultyLabel = (difficulty) => {
    if (!difficulty) return "";
    return difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
  };

  const chapterProgress = (() => {
    const byType = {
      mcqTotal: 0,
      mcqSolved: 0,
      mcqAttempted: 0,
      codingTotal: 0,
      codingSolved: 0,
      codingAttempted: 0,
      scoreEarned: 0,
      scoreTotal: 0,
    };

    for (const t of topics) {
      const meta = getTopicMeta(t);
      // We are only interested in scored topics for progress
      if (meta.type === "mcq") {
        byType.mcqTotal += 1;
        if (meta.attempted) byType.mcqAttempted += 1;
        if (meta.completed) byType.mcqSolved += 1; // Completed means earned >= totalPoints
        byType.scoreEarned += meta.earned;
        byType.scoreTotal += meta.totalPoints;
      } else if (meta.type === "coding") {
        byType.codingTotal += 1;
        if (meta.attempted) byType.codingAttempted += 1;
        if (meta.completed) byType.codingSolved += 1;
        byType.scoreEarned += meta.earned;
        byType.scoreTotal += meta.totalPoints;
      }
    }

    // Completion percentage based on SCORE
    const scorePercent = byType.scoreTotal > 0 ? Math.round((byType.scoreEarned / byType.scoreTotal) * 100) : 0;

    return {
      ...byType,
      percentCompleted: scorePercent, // Use score percent as the main completion metric
    };
  })();

  // Enable "Clear & Restart" when ALL problems are ATTEMPTED
  // (User request: enable even if not full score, provided all are attempted)
  const allProblemsAttempted =
    (chapterProgress.mcqTotal > 0 || chapterProgress.codingTotal > 0) &&
    (chapterProgress.mcqAttempted + chapterProgress.codingAttempted) === (chapterProgress.mcqTotal + chapterProgress.codingTotal);

  // Handle clear submissions
  const handleClearSubmissions = async () => {
    if (!isAuthenticated || !user?.token) {
      warningToast("Please login to clear submissions");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to clear all your submissions for this chapter? This will reset your progress and allow you to re-attempt all problems."
    );

    if (!confirmed) return;

    setIsClearingSubmissions(true);
    try {
      await clearChapterSubmissions(chapterId, user.token);
      successToast("Submissions cleared successfully!");
      // Reload the page to refresh progress
      window.location.reload();
    } catch (err) {
      console.error("Error clearing submissions:", err);
      errorToast(err?.response?.data?.error || "Failed to clear submissions");
    } finally {
      setIsClearingSubmissions(false);
    }
  };

  const handleDragStart = (index) => (e) => {
    setDraggedTopicIndex(index);
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(index));
    } catch {
      // no-op
    }
  };

  const handleDragOver = (overIndex) => (e) => {
    if (!isAdmin) return;
    e.preventDefault();
    if (draggedTopicIndex == null || draggedTopicIndex === overIndex) return;

    setTopics((prev) => {
      const next = [...prev];
      const [moved] = next.splice(draggedTopicIndex, 1);
      next.splice(overIndex, 0, moved);
      return next;
    });
    setDraggedTopicIndex(overIndex);
  };

  const getTopicId = (t) => String(t?.id || t?._id || "");

  const hasOrderChanges = (() => {
    if (!topicsOriginal || topicsOriginal.length === 0) return false;
    if (topics.length !== topicsOriginal.length) return true;
    for (let i = 0; i < topics.length; i += 1) {
      if (getTopicId(topics[i]) !== getTopicId(topicsOriginal[i])) return true;
    }
    return false;
  })();

  const buildTopicOrders = (current) => {
    const orders = {};
    current.forEach((t, idx) => {
      const id = t?.id || t?._id;
      if (id) orders[String(id)] = idx;
    });
    return orders;
  };

  const handleDragEnd = () => {
    if (!isAdmin) return;
    setDraggedTopicIndex(null);
  };

  const handleSaveOrder = async () => {
    if (!isAdmin) return;
    if (!user?.token) return;
    if (!hasOrderChanges) return;
    if (topics.length <= 1) return;

    const orders = buildTopicOrders(topics);

    setIsReordering(true);
    try {
      await reorderTopics(chapterId, orders, user.token);
      successToast("Topic order saved");
      setTopicsOriginal(topics);
    } catch (err) {
      console.error("Error reordering topics:", err);
      errorToast(err?.response?.data?.error || "Failed to reorder topics");
    } finally {
      setIsReordering(false);
    }
  };

  const handleDiscardOrder = () => {
    if (!isAdmin) return;
    setDraggedTopicIndex(null);
    setTopics(topicsOriginal);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="ca-container py-6 sm:py-8">
          <SkeletonTopicList />
        </div>
      </div>
    );
  }

  if (!chapter) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-900">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4 dark:text-white">Chapter not found</h2>
          <Link to="/courses" className="text-theme-primary font-medium">
            Go back to courses
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SeoMeta
        title={`${chapter.title} - Topics | Code Arena`}
        description={`Topics under ${chapter.title}`}
        url={`https://www.code-arena.in/chapter/${chapterId}`}
        image="/logo192.png"
      />

      <div className="ca-container py-6 sm:py-8">
        {/* Back Button */}
        {/* Updated logic: Go back to Course Details (Chapters List) */}
        <button
          onClick={() => navigate(`/courses/${chapter.courseId}`)}
          className="flex items-center gap-2 text-theme-primary hover:opacity-80 font-medium mb-6 transition-colors"
        >
          <FaArrowLeft /> Back to Chapters
        </button>

        {/* Chapter Header */}
        <div className="ca-card p-4 sm:p-8 mb-8">
          <h1 className="text-2xl sm:text-4xl font-bold mb-3 sm:mb-4">{chapter.title}</h1>
          <p className="text-base sm:text-lg text-gray-600 mb-5 sm:mb-6">{chapter.description}</p>

          {!chapterUnlock.unlocked && (
            <div className="mb-5 rounded-lg border border-gray-200 bg-gray-50 p-4" title={chapterUnlock.reason || ""}>
              <div className="flex items-start gap-3">
                <div className="text-gray-500 mt-0.5">
                  <FaLock />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">This chapter is locked</p>
                  <p className="text-sm text-gray-600 mt-1">{chapterUnlock.reason}</p>
                </div>
              </div>
            </div>
          )}

          {/* Progress Summary (reference-style) */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-theme-primary font-medium">
                {chapterProgress.percentCompleted}% completed
              </div>
              <div className="text-sm font-semibold text-gray-900 dark:text-white">
                Score: {chapterProgress.scoreEarned}/{chapterProgress.scoreTotal}
              </div>
            </div>

            <div className="w-full bg-gray-200 dark:bg-white/10 rounded-full h-2">
              <div
                className={`h-2 rounded-full bg-gradient-to-r ${currentTheme.colors.gradient}`}
                style={{ width: `${chapterProgress.percentCompleted}%` }}
              />
            </div>
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
              <span className="flex items-center gap-2">
                <FaQuestionCircle className="text-blue-500" />
                MCQ <span className="font-semibold text-gray-900 dark:text-white">{chapterProgress.mcqSolved}/{chapterProgress.mcqTotal}</span>
              </span>
              <span className="flex items-center gap-2">
                <FaCode className="text-orange-500" />
                Coding <span className="font-semibold text-gray-900 dark:text-white">{chapterProgress.codingSolved}/{chapterProgress.codingTotal}</span>
              </span>
            </div>

            {/* Clear Submissions Button */}
            {isAuthenticated && (chapterProgress.mcqTotal > 0 || chapterProgress.codingTotal > 0) && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleClearSubmissions}
                  disabled={!allProblemsAttempted || isClearingSubmissions}
                  className={`
                    inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium
                    transition-all duration-200
                    ${allProblemsAttempted
                      ? "bg-gradient-to-r from-red-500 to-rose-600 text-white shadow-md hover:shadow-lg hover:from-red-600 hover:to-rose-700"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    }
                  `}
                  title={
                    allProblemsAttempted
                      ? "Clear all submissions and restart this chapter"
                      : "Attempt all problems to enable restart option"
                  }
                >
                  <FaRedo className={`w-3.5 h-3.5 ${isClearingSubmissions ? "animate-spin" : ""}`} />
                  {isClearingSubmissions ? "Clearing..." : "Clear & Restart Chapter"}
                </button>
                {!allProblemsAttempted && (
                  <p className="text-xs text-gray-500 mt-2">
                    Attempt all problems to unlock restart option
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Topics Section */}
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              📚 Topics ({topics.length})
            </h2>

            {isAdmin && (
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <Link
                  to={`/topics/new?chapter=${chapterId}`}
                  className="ca-btn-primary w-full sm:w-auto text-sm"
                >
                  <FaPlus /> Add Topic
                </Link>
                <button
                  type="button"
                  onClick={handleSaveOrder}
                  disabled={isReordering || !hasOrderChanges}
                  className="ca-btn-primary w-full sm:w-auto text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  title={hasOrderChanges ? "Save topic order" : "No order changes"}
                >
                  {isReordering ? "Saving..." : "Reorder"}
                </button>
                <button
                  type="button"
                  onClick={handleDiscardOrder}
                  disabled={isReordering || !hasOrderChanges}
                  className="ca-btn-outline w-full sm:w-auto text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  title={hasOrderChanges ? "Discard changes" : "No order changes"}
                >
                  Discard
                </button>
              </div>
            )}
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap items-center gap-2 mb-6">
            {[
              { key: "all", label: "All", icon: null },
              { key: "theory", label: "Theory", icon: FaBook },
              { key: "video", label: "Video", icon: FaPlayCircle },
              { key: "mcq", label: "MCQ", icon: FaQuestionCircle },
              { key: "coding", label: "Coding", icon: FaCode },
            ].map(({ key, label, icon: Icon }) => {
              const isActive = activeFilter === key;
              const count = key === "all"
                ? topics.length
                : topics.filter((t) => t.type === key).length;

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveFilter(key)}
                  className={`
                    inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                    ${isActive
                      ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md"
                      : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:border-blue-400 hover:text-blue-600"
                    }
                  `}
                >
                  {Icon && <Icon className="text-xs" />}
                  {label}
                  <span className={`
                    px-1.5 py-0.5 rounded-full text-xs font-semibold
                    ${isActive ? "bg-white/20" : "bg-gray-100 dark:bg-gray-700"}
                  `}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Compute filtered topics */}
          {(() => {
            const filteredTopics = activeFilter === "all"
              ? topics
              : topics.filter((t) => t.type === activeFilter);

            if (filteredTopics.length === 0) {
              return (
                <div className="ca-card p-8 text-center">
                  <p className="text-gray-600 mb-4">
                    {topics.length === 0
                      ? "No topics yet"
                      : `No ${activeFilter} topics found`}
                  </p>
                  {loggedInUser?.role === "admin" && topics.length === 0 && (
                    <Link
                      to={`/topics/new?chapter=${chapterId}`}
                      className="ca-btn-primary"
                    >
                      <FaPlus /> Create First Topic
                    </Link>
                  )}
                  {activeFilter !== "all" && (
                    <button
                      type="button"
                      onClick={() => setActiveFilter("all")}
                      className="ca-btn-outline mt-2"
                    >
                      Show All Topics
                    </button>
                  )}
                </div>
              );
            }

            return (
              <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {filteredTopics.map((topic, index) => (
                  (() => {
                    const meta = getTopicMeta(topic);
                    const showProgress = meta.type === "mcq" || meta.type === "coding";
                    const statusText = meta.completed
                      ? "Completed"
                      : meta.attempted
                        ? "Attempted"
                        : "Not attempted";
                    const statusClass = meta.completed
                      ? "text-green-700 bg-green-50 border-green-200"
                      : meta.attempted
                        ? "text-orange-700 bg-orange-50 border-orange-200"
                        : "text-gray-600 bg-gray-50 border-gray-200";

                    const pointsText = meta.totalPoints > 0 ? `${meta.earned}/${meta.totalPoints}` : "-";

                    return (
                      <div
                        key={topic?.id || topic?._id || `${index}`}
                        onDragOver={handleDragOver(index)}
                        onDrop={(e) => {
                          if (!isAdmin) return;
                          e.preventDefault();
                        }}
                        className={`ca-card ca-card-hover overflow-hidden flex flex-col cursor-pointer ${draggedTopicIndex === index ? "opacity-60" : ""
                          }`}
                        onClick={() => {
                          if (!chapterUnlock.unlocked && !isAdmin) return;
                          const filterParam = activeFilter !== "all" ? `?filter=${activeFilter}` : "";
                          navigate(`/topic/${topic.id}${filterParam}`);
                        }}
                        title={!chapterUnlock.unlocked && !isAdmin ? chapterUnlock.reason || "" : ""}
                      >
                        <div className="p-4 flex flex-col gap-3 flex-1">
                          {/* Top row */}
                          <div className="flex items-start justify-between gap-3">
                            <h3 className="text-base sm:text-lg font-semibold text-gray-900 leading-snug line-clamp-2">
                              {topic.title}
                            </h3>

                            {!chapterUnlock.unlocked && !isAdmin && (
                              <span className="text-gray-400 mt-0.5" title={chapterUnlock.reason || ""}>
                                <FaLock />
                              </span>
                            )}

                            {isAdmin && (
                              <span
                                draggable
                                onClick={(e) => e.stopPropagation()}
                                onDragStart={handleDragStart(index)}
                                onDragEnd={handleDragEnd}
                                className="text-gray-400 cursor-grab active:cursor-grabbing mt-0.5"
                                title="Drag to reorder"
                              >
                                <FaGripVertical />
                              </span>
                            )}
                          </div>

                          {/* Badges */}
                          <div className="flex flex-wrap items-center gap-2">
                            {(() => {
                              const typeStyle = getTypeStyle(meta.type);
                              const TypeIcon = typeStyle.icon;
                              return (
                                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border inline-flex items-center gap-1.5 ${typeStyle.bg} ${typeStyle.text} ${typeStyle.border}`}>
                                  <TypeIcon className="text-[10px]" />
                                  {typeLabel(meta.type)}
                                </span>
                              );
                            })()}

                            {meta.type === "coding" && meta.difficulty && (
                              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getDifficultyStyle(meta.difficulty)}`}>
                                {difficultyLabel(meta.difficulty)}
                              </span>
                            )}
                          </div>

                          {/* Progress (only for MCQ/Coding; theory/video are not trackable) */}
                          {showProgress && (
                            <div className="flex items-center justify-between gap-3 mt-1">
                              <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs font-semibold border ${statusClass}`}>
                                <span className={meta.completed ? "text-green-600" : meta.attempted ? "text-orange-600" : "text-gray-400"}>
                                  {meta.completed ? "✓" : meta.attempted ? "•" : "○"}
                                </span>
                                <span>{statusText}</span>
                              </div>

                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-700 font-semibold">{pointsText}</span>
                                <span className={meta.completed ? "text-green-600 font-bold" : "text-gray-300 font-bold"}>
                                  {meta.completed ? "✓" : "○"}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Admin actions */}
                          {isAdmin && (
                            <div className="flex gap-2 mt-auto">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/topics/edit/${topic.id}`);
                                }}
                                className="ca-btn-outline flex-1 text-sm"
                              >
                                Edit
                              </button>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTopic(topic.id);
                                }}
                                disabled={deletingTopic === topic.id}
                                className="ca-btn-danger flex-1 text-sm"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()
                ))}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
